import { useEffect, useMemo, useState } from "react";
import {
  DOMAIN_LABELS,
  type Dependency,
  type DomainId,
  type GradeBand,
  type Topic,
  diagnosticTopics,
  loadDataset,
  prerequisites,
  topicsByAnchor,
  topicsByStandard,
  unlocks
} from "./dataAccess";

const dataset = loadDataset();
const gradeOptions: GradeBand[] = ["G1", "G2", "G3", "G4", "G5", "G6"];
const domainOptions = Object.keys(DOMAIN_LABELS) as DomainId[];
const domainOrder = domainOptions;

type ViewMode = "overview" | "focus";
type RelationStatus = "connected" | "root-or-underlinked" | "leaf-or-underlinked" | "isolated-underlinked";
type OverviewDisplayMode = "all" | "related" | "isolated";

type RelationStats = {
  totalTopics: number;
  totalEdges: number;
  isolated: number;
  noPrerequisites: number;
  noUnlocks: number;
  connected: number;
};

const anchorName = (anchorId: string) =>
  dataset.capabilityAnchors.find((anchor) => anchor.id === anchorId)?.name ?? anchorId;

const standardTitle = (standardId: string) =>
  dataset.standards.find((standard) => standard.id === standardId)?.title ?? standardId;

function App() {
  const initialTopicId = diagnosticTopics("G2")[0]?.topicId ?? diagnosticTopics()[0]?.topicId ?? "";
  const [selectedTopicId, setSelectedTopicId] = useState(initialTopicId);
  const [viewMode, setViewMode] = useState<ViewMode>("overview");
  const [gradeBand, setGradeBand] = useState<GradeBand | "all">("all");
  const [domainId, setDomainId] = useState<DomainId | "all">("all");
  const [capabilityAnchorId, setCapabilityAnchorId] = useState<string>("all");
  const [standardId, setStandardId] = useState<string>("all");
  const [recursive, setRecursive] = useState(false);
  const [relationStatusFilter, setRelationStatusFilter] = useState<RelationStatus | "all">("all");

  const filteredTopics = useMemo(() => {
    const gradeTopics =
      capabilityAnchorId === "all"
        ? diagnosticTopics(gradeBand === "all" ? undefined : gradeBand)
        : topicsByAnchor(capabilityAnchorId).filter(
            (topic) => gradeBand === "all" || topic.gradeBand === gradeBand
          );
    const gradeTopicIds = new Set(gradeTopics.map((topic) => topic.topicId));
    const standardTopics =
      standardId === "all"
        ? gradeTopics
        : topicsByStandard(standardId).filter((topic) => gradeTopicIds.has(topic.topicId));

    return standardTopics.filter((topic) => domainId === "all" || topic.domainIds.includes(domainId));
  }, [capabilityAnchorId, domainId, gradeBand, standardId]);

  useEffect(() => {
    if (filteredTopics.length > 0 && !filteredTopics.some((topic) => topic.topicId === selectedTopicId)) {
      setSelectedTopicId(filteredTopics[0].topicId);
    }
  }, [filteredTopics, selectedTopicId]);

  const selectedTopic = filteredTopics.find((topic) => topic.topicId === selectedTopicId) ?? filteredTopics[0];
  const filteredTopicIds = useMemo(() => new Set(filteredTopics.map((topic) => topic.topicId)), [filteredTopics]);
  const filteredEdges = useMemo(
    () =>
      dataset.dependencies.filter(
        (edge) => filteredTopicIds.has(edge.topicId) && filteredTopicIds.has(edge.prerequisiteId)
      ),
    [filteredTopicIds]
  );
  const relationStats = useMemo(() => buildRelationStats(filteredTopics, filteredEdges), [filteredTopics, filteredEdges]);
  const displayTopics = useMemo(
    () =>
      relationStatusFilter === "all"
        ? filteredTopics
        : filteredTopics.filter((topic) => relationStatus(topic.topicId, filteredEdges) === relationStatusFilter),
    [filteredTopics, filteredEdges, relationStatusFilter]
  );
  const displayTopicIds = useMemo(() => new Set(displayTopics.map((topic) => topic.topicId)), [displayTopics]);
  const displayEdges = useMemo(
    () => filteredEdges.filter((edge) => displayTopicIds.has(edge.topicId) && displayTopicIds.has(edge.prerequisiteId)),
    [filteredEdges, displayTopicIds]
  );
  const selectedPrerequisites = selectedTopic ? prerequisites(selectedTopic.topicId, recursive) : [];
  const selectedUnlocks = selectedTopic ? unlocks(selectedTopic.topicId, recursive) : [];

  const activeAnchors = dataset.capabilityAnchors.filter(
    (anchor) => diagnosticTopics().some((topic) => topic.capabilityAnchor === anchor.id)
  );
  const activeStandards = dataset.standards.filter(
    (standard) => diagnosticTopics().some((topic) => topic.standards.includes(standard.id))
  );

  const applyStandardFilter = (nextStandardId: string) => {
    setStandardId(nextStandardId);
    setRelationStatusFilter("all");
    setViewMode("overview");
  };

  return (
    <main className="appShell">
      <section className="masthead">
        <div>
          <p className="eyebrow">Reference application / candidate data</p>
          <h1>小学数学知识图谱浏览器</h1>
          <p className="intro">
            一个静态示例应用，演示应用层如何通过稳定 ID、能力锚点、课标映射和 soft 依赖边消费公开数据。
          </p>
        </div>
        <div className="versionPanel" aria-label="数据版本与候选状态">
          <span>datasetVersion</span>
          <strong>{dataset.datasetVersion}</strong>
          <small>{dataset.status}</small>
        </div>
      </section>

      <section className="noticeGrid" aria-label="候选数据提示">
        <Notice title="candidate 状态" body="当前数据是候选发布层，适合接口验证和浏览，不等同正式教学产品。" />
        <Notice title="非专家确认" body="当前候选图谱未完成独立专家确认，不应声明为专家确认学习路径。" />
        <Notice title="soft hypotheses" body="本应用展示的所有边均为 soft dependency hypotheses，仅表示有帮助的前置经验。" />
      </section>

      <section className="workspace">
        <aside className="controls" aria-label="筛选条件">
          <div className="viewSwitch" aria-label="图谱视图模式">
            <button
              className={viewMode === "overview" ? "active" : ""}
              onClick={() => setViewMode("overview")}
              type="button"
            >
              Overview
            </button>
            <button className={viewMode === "focus" ? "active" : ""} onClick={() => setViewMode("focus")} type="button">
              Focus
            </button>
          </div>

          <label className="field">
            <span>年级</span>
            <select value={gradeBand} onChange={(event) => setGradeBand(event.target.value as GradeBand | "all")}>
              <option value="all">全部年级</option>
              {gradeOptions.map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>领域</span>
            <select value={domainId} onChange={(event) => setDomainId(event.target.value as DomainId | "all")}>
              <option value="all">全部领域</option>
              {domainOptions.map((domain) => (
                <option key={domain} value={domain}>
                  {DOMAIN_LABELS[domain]}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>能力锚点</span>
            <select value={capabilityAnchorId} onChange={(event) => setCapabilityAnchorId(event.target.value)}>
              <option value="all">全部能力锚点</option>
              {activeAnchors.map((anchor) => (
                <option key={anchor.id} value={anchor.id}>
                  {anchor.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>课程标准</span>
            <select value={standardId} onChange={(event) => applyStandardFilter(event.target.value)}>
              <option value="all">全部课程标准</option>
              {activeStandards.map((standard) => (
                <option key={standard.id} value={standard.id}>
                  {standard.title}
                </option>
              ))}
            </select>
          </label>

          <label className="toggleRow">
            <input type="checkbox" checked={recursive} onChange={(event) => setRecursive(event.target.checked)} />
            <span>递归展示 prerequisites / unlocks</span>
          </label>

          <section className="integrityBlock" aria-labelledby="integrity-title">
            <p className="eyebrow">Current filtered scope</p>
            <h2 id="integrity-title">关系完整性</h2>
            <div className="integrityGrid">
              <Metric label="诊断节点" value={relationStats.totalTopics} />
              <Metric label="依赖边" value={relationStats.totalEdges} />
              <Metric label="孤立节点" value={relationStats.isolated} active={relationStatusFilter === "isolated-underlinked"} onClick={() => toggleStatusFilter("isolated-underlinked", relationStatusFilter, setRelationStatusFilter)} />
              <Metric label="无前置" value={relationStats.noPrerequisites} active={relationStatusFilter === "root-or-underlinked"} onClick={() => toggleStatusFilter("root-or-underlinked", relationStatusFilter, setRelationStatusFilter)} />
              <Metric label="无后续" value={relationStats.noUnlocks} active={relationStatusFilter === "leaf-or-underlinked"} onClick={() => toggleStatusFilter("leaf-or-underlinked", relationStatusFilter, setRelationStatusFilter)} />
              <Metric label="已连接" value={relationStats.connected} active={relationStatusFilter === "connected"} onClick={() => toggleStatusFilter("connected", relationStatusFilter, setRelationStatusFilter)} />
            </div>
            {relationStatusFilter !== "all" && <button className="clearStatusFilter" onClick={() => setRelationStatusFilter("all")} type="button">清除关系状态筛选</button>}
          </section>
        </aside>

        <section className="mainColumn">
          {viewMode === "overview" ? (
            <OverviewGraph
              topics={displayTopics}
              edges={displayEdges}
              statusEdges={filteredEdges}
              selectedTopicId={selectedTopic?.topicId}
              onSelect={setSelectedTopicId}
            />
          ) : selectedTopic ? (
            <FocusGraph
              center={selectedTopic}
              prerequisiteTopics={selectedPrerequisites.map((item) => item.topic)}
              unlockTopics={selectedUnlocks.map((item) => item.topic)}
              onSelect={setSelectedTopicId}
            />
          ) : (
            <EmptyGraph />
          )}

          <section className="topicList" aria-label="诊断节点列表">
            <div className="sectionHeader">
              <div>
                <p className="eyebrow">Diagnostic layer only</p>
                <h2>节点列表</h2>
              </div>
              <span className="countPill">{filteredTopics.length} nodes</span>
            </div>
            <div className="listScroller">
              {displayTopics.map((topic) => {
                const status = relationStatus(topic.topicId, filteredEdges);
                return (
                  <button
                    className={`topicButton ${topic.topicId === selectedTopic?.topicId ? "selected" : ""}`}
                    key={topic.topicId}
                    onClick={() => setSelectedTopicId(topic.topicId)}
                    type="button"
                  >
                    <span className="topicName">{topic.displayName}</span>
                    <span className="topicMeta">
                      {topic.gradeBand} · {topic.domainIds.map((id) => DOMAIN_LABELS[id]).join(" / ")}
                    </span>
                    <span className={`statusTag ${status}`}>{relationStatusLabel(status)}</span>
                    <code>{topic.topicId}</code>
                  </button>
                );
              })}
              {displayTopics.length === 0 && <p className="emptyState">当前关系状态筛选下没有诊断节点。</p>}
            </div>
          </section>
        </section>

        <DetailPanel
          topic={selectedTopic}
          prerequisiteItems={selectedPrerequisites}
          unlockItems={selectedUnlocks}
          onSelect={setSelectedTopicId}
          onFilterByStandard={applyStandardFilter}
        />
      </section>
    </main>
  );
}

function buildRelationStats(topics: Topic[], edges: Dependency[]): RelationStats {
  const statuses = topics.map((topic) => relationStatus(topic.topicId, edges));
  return {
    totalTopics: topics.length,
    totalEdges: edges.length,
    isolated: statuses.filter((status) => status === "isolated-underlinked").length,
    noPrerequisites: topics.filter((topic) => !edges.some((edge) => edge.topicId === topic.topicId)).length,
    noUnlocks: topics.filter((topic) => !edges.some((edge) => edge.prerequisiteId === topic.topicId)).length,
    connected: statuses.filter((status) => status === "connected").length
  };
}

function relationStatus(topicId: string, edges: Dependency[]): RelationStatus {
  const hasPrerequisites = edges.some((edge) => edge.topicId === topicId);
  const hasUnlocks = edges.some((edge) => edge.prerequisiteId === topicId);
  if (hasPrerequisites && hasUnlocks) return "connected";
  if (hasUnlocks) return "root-or-underlinked";
  if (hasPrerequisites) return "leaf-or-underlinked";
  return "isolated-underlinked";
}

function relationStatusLabel(status: RelationStatus) {
  return {
    connected: "已连接",
    "root-or-underlinked": "无前置 / 待核",
    "leaf-or-underlinked": "无后续 / 待核",
    "isolated-underlinked": "暂无已建模关系"
  }[status];
}

function toggleStatusFilter(
  status: RelationStatus,
  currentFilter: RelationStatus | "all",
  setFilter: (filter: RelationStatus | "all") => void
) {
  setFilter(currentFilter === status ? "all" : status);
}

function Notice({ title, body }: { title: string; body: string }) {
  return <article className="notice"><strong>{title}</strong><p>{body}</p></article>;
}

function Metric({ label, value, active, onClick }: { label: string; value: number; active?: boolean; onClick?: () => void }) {
  if (onClick) {
    return <button className={`metric metricButton ${active ? "active" : ""}`} onClick={onClick} type="button"><strong>{value}</strong><span>{label}</span></button>;
  }
  return <div className="metric"><strong>{value}</strong><span>{label}</span></div>;
}

function OverviewGraph({
  topics,
  edges,
  statusEdges,
  selectedTopicId,
  onSelect
}: {
  topics: Topic[];
  edges: Dependency[];
  statusEdges: Dependency[];
  selectedTopicId?: string;
  onSelect: (topicId: string) => void;
}) {
  const [displayMode, setDisplayMode] = useState<OverviewDisplayMode>("all");
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const relatedTopics = useMemo(
    () => topics.filter((topic) => relationStatus(topic.topicId, statusEdges) !== "isolated-underlinked"),
    [topics, statusEdges]
  );
  const isolatedTopics = useMemo(
    () => topics.filter((topic) => relationStatus(topic.topicId, statusEdges) === "isolated-underlinked"),
    [topics, statusEdges]
  );
  const graphTopics = displayMode === "isolated" ? [] : relatedTopics;
  const graphTopicIds = new Set(graphTopics.map((topic) => topic.topicId));
  const graphEdges = edges.filter((edge) => graphTopicIds.has(edge.topicId) && graphTopicIds.has(edge.prerequisiteId));
  const layout = useMemo(() => createOverviewLayout(graphTopics), [graphTopics]);
  const byId = new Map(layout.nodes.map((node) => [node.topic.topicId, node]));
  const selectedNeighborIds = useMemo(() => {
    if (!selectedTopicId) return new Set<string>();
    return new Set(
      statusEdges
        .filter((edge) => edge.topicId === selectedTopicId || edge.prerequisiteId === selectedTopicId)
        .map((edge) => (edge.topicId === selectedTopicId ? edge.prerequisiteId : edge.topicId))
    );
  }, [selectedTopicId, statusEdges]);

  if (topics.length === 0) return <EmptyGraph />;

  return (
    <section className="overviewPanel" aria-label="整体知识图谱视图">
      <div className="sectionHeader">
        <div>
          <p className="eyebrow">Overview graph</p>
          <h2>整体知识图谱</h2>
          <p className="graphCaption">箭头方向：prerequisite → topic。主图优先呈现已登记关系；暂无关系的节点进入下方审计区，不被解释为基础节点。</p>
        </div>
        <span className="countPill">{topics.length} nodes / {edges.length} edges</span>
      </div>
      <div className="overviewToolbar">
        <div className="graphModeSwitch" aria-label="Overview 图谱显示模式">
          <button className={displayMode === "all" ? "active" : ""} onClick={() => setDisplayMode("all")} type="button">全部节点</button>
          <button className={displayMode === "related" ? "active" : ""} onClick={() => setDisplayMode("related")} type="button">仅有边节点</button>
          <button className={displayMode === "isolated" ? "active" : ""} onClick={() => setDisplayMode("isolated")} type="button">孤立节点审计</button>
        </div>
        <div className="mapTools" aria-label="图谱视图控制">
          <button aria-label="缩小图谱" onClick={() => setZoom((value) => Math.max(0.7, Number((value - 0.15).toFixed(2))))} title="缩小" type="button">−</button>
          <span>{Math.round(zoom * 100)}%</span>
          <button aria-label="放大图谱" onClick={() => setZoom((value) => Math.min(1.8, Number((value + 0.15).toFixed(2))))} title="放大" type="button">+</button>
          <button className="resetMap" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} type="button">重置</button>
        </div>
      </div>
      <div className="legend" aria-label="节点关系状态图例">
        <LegendItem status="connected" label="已连接" />
        <LegendItem status="root-or-underlinked" label="无前置 / 待核" />
        <LegendItem status="leaf-or-underlinked" label="无后续 / 待核" />
        <LegendItem status="isolated-underlinked" label="暂无已建模关系" />
      </div>
      {displayMode !== "isolated" && <div className="overviewScroller">
        <svg
          className="overviewSvg"
          onPointerDown={(event) => setDragStart({ x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y })}
          onPointerMove={(event) => {
            if (dragStart) setPan({ x: dragStart.panX + (event.clientX - dragStart.x) / zoom, y: dragStart.panY + (event.clientY - dragStart.y) / zoom });
          }}
          onPointerUp={() => setDragStart(null)}
          onPointerLeave={() => setDragStart(null)}
          onWheel={(event) => { event.preventDefault(); setZoom((value) => Math.max(0.7, Math.min(1.8, Number((value + (event.deltaY < 0 ? 0.1 : -0.1)).toFixed(2))))); }}
          viewBox={`0 0 ${layout.width} ${layout.height}`}
          role="img"
          aria-labelledby="overview-title"
        >
          <title id="overview-title">当前筛选范围内全部诊断节点及其依赖关系</title>
          <defs>
            <marker id="overview-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" />
            </marker>
          </defs>
          <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
          {layout.domains.map((domain, index) => (
            <g key={domain}>
              <rect className="domainLane" x="0" y={layout.top + index * layout.laneHeight} width={layout.width} height={layout.laneHeight} />
              <text className="domainLabel" x="18" y={layout.top + index * layout.laneHeight + 28}>{DOMAIN_LABELS[domain]}</text>
            </g>
          ))}
          {layout.grades.map((grade, index) => (
            <g key={grade}>
              <rect className="gradeHeader" x={layout.left + index * layout.cellWidth} y="0" width={layout.cellWidth} height={layout.top} />
              <text className="gradeLabel" x={layout.left + index * layout.cellWidth + layout.cellWidth / 2} y="30">{grade}</text>
            </g>
          ))}
          {graphEdges.map((edge) => {
            const prerequisite = byId.get(edge.prerequisiteId);
            const topic = byId.get(edge.topicId);
            if (!prerequisite || !topic) return null;
            const isSelectedEdge = edge.topicId === selectedTopicId || edge.prerequisiteId === selectedTopicId;
            return <line className={`overviewEdge ${selectedTopicId ? (isSelectedEdge ? "selected" : "muted") : ""}`} key={`${edge.topicId}-${edge.prerequisiteId}`} x1={prerequisite.x} y1={prerequisite.y} x2={topic.x} y2={topic.y} />;
          })}
          {layout.nodes.map(({ topic, x, y }) => {
            const status = relationStatus(topic.topicId, statusEdges);
            const isSelected = topic.topicId === selectedTopicId;
            const isNeighbor = selectedNeighborIds.has(topic.topicId);
            const isMuted = Boolean(selectedTopicId && !isSelected && !isNeighbor);
            return (
              <g
                aria-label={`${topic.displayName}，${relationStatusLabel(status)}`}
                className={`overviewNode ${status} ${isSelected ? "selected" : ""} ${isNeighbor ? "neighbor" : ""} ${isMuted ? "muted" : ""}`}
                key={topic.topicId}
                onClick={() => onSelect(topic.topicId)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") onSelect(topic.topicId);
                }}
                role="button"
                tabIndex={0}
              >
                <title>{`${topic.displayName}\n${topic.gradeBand} · ${topic.domainIds.map((domain) => DOMAIN_LABELS[domain]).join(" / ")}\n${relationStatusLabel(status)}`}</title>
                <circle cx={x} cy={y} r={isSelected ? 15 : isNeighbor ? 12 : 10} />
                {isSelected && <text className="selectedNodeLabel" x={x + 14} y={y - 12}>{topic.displayName}</text>}
              </g>
            );
          })}
          {graphTopics.length === 0 && <text className="mapEmptyText" x={layout.width / 2} y={layout.height / 2}>当前范围没有已登记依赖边</text>}
          </g>
        </svg>
        <p className="panHint">滚轮缩放，拖拽平移。选择节点后，直连的一阶关系会高亮。</p>
      </div>}
      {(displayMode === "all" || displayMode === "isolated") && <IsolatedAudit topics={isolatedTopics} open={displayMode === "isolated" || auditOpen} onToggle={() => setAuditOpen((value) => !value)} onSelect={onSelect} />}
    </section>
  );
}

function createOverviewLayout(topics: Topic[]) {
  const grades = gradeOptions.filter((grade) => topics.some((topic) => topic.gradeBand === grade));
  const domains = domainOrder.filter((domain) => topics.some((topic) => topic.domainIds.includes(domain)));
  const left = 128;
  const top = 62;
  const cellWidth = 238;
  const laneHeight = 188;
  const width = left + Math.max(grades.length, 1) * cellWidth;
  const height = top + Math.max(domains.length, 1) * laneHeight;
  const nodes = topics
    .slice()
    .sort((a, b) => a.topicId.localeCompare(b.topicId))
    .map((topic) => {
      const gradeIndex = Math.max(grades.indexOf(topic.gradeBand), 0);
      const domainIndex = Math.max(domains.findIndex((domain) => topic.domainIds.includes(domain)), 0);
      const cellTopics = topics
        .filter((candidate) => candidate.gradeBand === topic.gradeBand && candidate.domainIds.some((domain) => topic.domainIds.includes(domain)))
        .slice()
        .sort((a, b) => a.topicId.localeCompare(b.topicId));
      const itemIndex = cellTopics.findIndex((candidate) => candidate.topicId === topic.topicId);
      const columns = 4;
      const x = left + gradeIndex * cellWidth + 38 + (itemIndex % columns) * 51;
      const y = top + domainIndex * laneHeight + 64 + Math.floor(itemIndex / columns) * 36;
      return { topic, x, y };
    });
  return { grades, domains, left, top, cellWidth, laneHeight, width, height, nodes };
}

function IsolatedAudit({ topics, open, onToggle, onSelect }: { topics: Topic[]; open: boolean; onToggle: () => void; onSelect: (topicId: string) => void }) {
  const groups = gradeOptions
    .map((grade) => ({ grade, topics: topics.filter((topic) => topic.gradeBand === grade) }))
    .filter((group) => group.topics.length > 0);
  return <section className="isolatedAudit" aria-label="孤立节点审计">
    <div className="auditHeader"><div><p className="eyebrow">Relationship audit</p><h3>暂无已建模关系</h3><p>{topics.length} 个节点当前既无已登记前置，也无已登记后续。这是待补关系信号，不代表基础节点。</p></div><button onClick={onToggle} type="button">{open ? "折叠审计列表" : "展开审计列表"}</button></div>
    {open && <div className="auditGroups">{groups.map((group) => <section key={group.grade}><h4>{group.grade} <span>{group.topics.length}</span></h4><div>{group.topics.map((topic) => <button key={topic.topicId} onClick={() => onSelect(topic.topicId)} title={`${topic.gradeBand} · ${topic.domainIds.map((domain) => DOMAIN_LABELS[domain]).join(" / ")}`} type="button">{topic.displayName}</button>)}</div></section>)}</div>}
  </section>;
}

function LegendItem({ status, label }: { status: RelationStatus; label: string }) {
  return <span><i className={`legendDot ${status}`} />{label}</span>;
}

function EmptyGraph() {
  return <section className="overviewPanel emptyGraph"><h2>当前没有可展示的诊断节点</h2><p>调整筛选条件后，整体图谱会显示命中的节点与其已登记边。</p></section>;
}

function DetailPanel({
  topic,
  prerequisiteItems,
  unlockItems,
  onSelect,
  onFilterByStandard
}: {
  topic?: Topic;
  prerequisiteItems: ReturnType<typeof prerequisites>;
  unlockItems: ReturnType<typeof unlocks>;
  onSelect: (topicId: string) => void;
  onFilterByStandard: (standardId: string) => void;
}) {
  if (!topic) return <section className="detailPanel"><p className="emptyState">选择一个诊断节点以查看详情。</p></section>;
  return (
    <section className="detailPanel" aria-label="节点详情">
      <div className="sectionHeader">
        <div><p className="eyebrow">Topic detail</p><h2>{topic.displayName}</h2></div>
        <span className="layerBadge">diagnostic</span>
      </div>
      <p className="description">{topic.displayDescription}</p>
      <dl className="idGrid">
        <div><dt>topicId</dt><dd>{topic.topicId}</dd></div>
        <div><dt>capabilityAnchor</dt><dd>{topic.capabilityAnchor}</dd></div>
        <div><dt>数据版本</dt><dd>{dataset.datasetVersion}</dd></div>
      </dl>
      <div className="anchorBlock"><h3>能力锚点</h3><p>{anchorName(topic.capabilityAnchor)}</p></div>
      <div className="standardBlock">
        <h3>课程标准映射</h3>
        {topic.standards.map((topicStandardId) => {
          const matchingTopics = topicsByStandard(topicStandardId);
          return <button className="standardButton" key={topicStandardId} onClick={() => onFilterByStandard(topicStandardId)} type="button">
            <span>{standardTitle(topicStandardId)}</span>
            <small>浏览该课标下全部 {matchingTopics.length} 个 diagnostic 节点</small>
            <code>{topicStandardId}</code>
          </button>;
        })}
      </div>
      <div className="relationGrid">
        <RelationList title="Prerequisites" emptyText="当前图谱未登记前置能力。" items={prerequisiteItems} onSelect={onSelect} />
        <RelationList title="Unlocks" emptyText="当前图谱未登记后续解锁目标。" items={unlockItems} onSelect={onSelect} />
      </div>
    </section>
  );
}

function RelationList({ title, emptyText, items, onSelect }: { title: string; emptyText: string; items: ReturnType<typeof prerequisites>; onSelect: (topicId: string) => void }) {
  return <section className="relationList"><h3>{title}</h3>{items.length === 0 && <p className="emptyState">{emptyText}</p>}{items.map(({ edge, topic }) => (
    <button className="relationItem" key={`${edge.topicId}-${edge.prerequisiteId}`} onClick={() => onSelect(topic.topicId)} type="button">
      <span><strong>{topic.displayName}</strong><small>{anchorName(topic.capabilityAnchor)}</small></span><span className="edgeChip">{edge.strength} · candidate</span>
    </button>
  ))}</section>;
}

function FocusGraph({ center, prerequisiteTopics, unlockTopics, onSelect }: { center: Topic; prerequisiteTopics: Topic[]; unlockTopics: Topic[]; onSelect: (topicId: string) => void }) {
  const prereqRows = layoutRows(prerequisiteTopics);
  const unlockRows = layoutRows(unlockTopics);
  return <section className="overviewPanel" aria-label="单节点关系视图">
    <div className="sectionHeader"><div><p className="eyebrow">Focus graph</p><h2>单节点关系</h2><p className="graphCaption">显示所选节点的 prerequisites 与 unlocks。递归开关会扩展关系列表。</p></div></div>
    <div className="focusScroller"><svg className="focusSvg" viewBox="0 0 920 320" role="img" aria-labelledby="focus-title"><title id="focus-title">当前节点的前置与解锁关系</title><defs><marker id="focus-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto"><path d="M 0 0 L 10 5 L 0 10 z" /></marker></defs>
      {prereqRows.map((row) => <line className="focusEdge" key={`pre-line-${row.topic.topicId}`} x1="260" y1={row.y} x2="430" y2="160" />)}
      {unlockRows.map((row) => <line className="focusEdge" key={`unlock-line-${row.topic.topicId}`} x1="490" y1="160" x2="660" y2={row.y} />)}
      {prereqRows.map((row) => <FocusNode key={row.topic.topicId} x={48} y={row.y - 28} topic={row.topic} onSelect={onSelect} tone="pre" />)}
      <FocusNode x={360} y={112} topic={center} onSelect={onSelect} tone="center" />
      {unlockRows.map((row) => <FocusNode key={row.topic.topicId} x={662} y={row.y - 28} topic={row.topic} onSelect={onSelect} tone="unlock" />)}
      <text className="graphLabel" x="48" y="38">prerequisites</text><text className="graphLabel" x="382" y="92">selected topic</text><text className="graphLabel" x="662" y="38">unlocks</text>
    </svg></div>
  </section>;
}

function FocusNode({ x, y, topic, tone, onSelect }: { x: number; y: number; topic: Topic; tone: "pre" | "center" | "unlock"; onSelect: (topicId: string) => void }) {
  return <g className={`focusNode ${tone}`} tabIndex={0} role="button" onClick={() => onSelect(topic.topicId)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onSelect(topic.topicId); }}><rect x={x} y={y} width="210" height="56" rx="7" /><text x={x + 14} y={y + 24}>{topic.displayName.slice(0, 14)}</text><text className="nodeSubline" x={x + 14} y={y + 42}>{topic.gradeBand} · {DOMAIN_LABELS[topic.domainIds[0]]}</text></g>;
}

function layoutRows(topics: Topic[]) {
  const visibleTopics = topics.slice(0, 4);
  const step = visibleTopics.length > 2 ? 66 : 82;
  const start = 160 - ((visibleTopics.length - 1) * step) / 2;
  return visibleTopics.map((topic, index) => ({ topic, y: start + index * step }));
}

export default App;
